import { Injectable } from '@angular/core';
import {
  HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';
import { SELF_HANDLED_429_URL, retryAfterSeconds, waitText } from '../utils/retry-after';

/**
 * ERROR INTERCEPTOR
 * Centralized error classification + toast display.
 *
 * Responsibilities:
 *  - Classify HTTP errors by status code
 *  - Show appropriate toast with human-friendly message
 *  - Skip errors already handled by other interceptors:
 *      · 401 → handled by AuthInterceptor (logout)
 *      · status 0 on write requests → handled by NetworkGuardService
 *      · 403 mustChangePassword / requiredFeature → handled by AuthInterceptor
 *      · 429 from sign-in, password reset and renewal → those pages show the wait themselves (R1)
 *
 * Opt-out per request: set header `X-No-Toast: true`
 *   (use when component wants to render its own error UI, e.g. 404 empty state)
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(private toast: ToastService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const suppressed = req.headers.has('X-No-Toast');
    const stripped = suppressed
      ? req.clone({ headers: req.headers.delete('X-No-Toast') })
      : req;

    return next.handle(stripped).pipe(
      catchError((err: any) => {
        if (suppressed) return throwError(() => err);

        // Duplicate-request block (DedupeRequestInterceptor) — silent SWALLOW.
        // The user clicked twice rapidly; the second click was discarded to
        // prevent duplicate writes. No toast needed AND no component error
        // handler invocation — the first click is still in flight and will
        // surface its own success/error on its own subscription.
        //
        // CHANGED May 2026: was re-throwing here, but components running
        // `if (err.status === 0) queueRequest(...)` were misinterpreting the
        // dedupe block as a network failure and queueing the duplicate for
        // offline replay. Returning EMPTY now means component error handlers
        // NEVER see dedupe-blocks — only true HTTP errors. The blocked
        // subscription completes silently.
        if (err?.error?.code === 'DUPLICATE_REQUEST'
            || err?.statusText === 'DUPLICATE_BLOCKED') {
          return EMPTY;
        }

        // Timeout from TimeoutInterceptor — not HttpErrorResponse
        if (err?.name === 'TimeoutError') {
          this.toast.error(
            'Request timed out — the server is taking too long. Please try again.',
            { label: 'Dismiss', callback: () => {} }
          );
          return throwError(() => err);
        }

        if (!(err instanceof HttpErrorResponse)) {
          this.toast.error('An unexpected error occurred');
          return throwError(() => err);
        }

        const isWrite = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method.toUpperCase());
        const serverMsg = this.extractServerMessage(err);

        switch (err.status) {
          case 0:
            // status 0 on WRITE is handled by NetworkGuardService.
            // Show toast ONLY for reads (so user knows something went wrong).
            if (!isWrite) {
              this.toast.error('Network error — please check your connection', {
                label: 'Retry',
                callback: () => window.location.reload()
              });
            }
            break;

          case 401:
            // Handled by AuthInterceptor (logout). No toast here.
            break;

          case 403:
            // mustChangePassword / requiredFeature handled by AuthInterceptor. TC-0126: ACCOUNT_SETUP_INVALID (sign-in
            // refused for a role that is not a hospital role) is shown by the sign-in page itself.
            if (!err.error?.mustChangePassword && !err.error?.requiredFeature && err.error?.code !== 'ACCOUNT_SETUP_INVALID') {
              this.toast.error(
                serverMsg || "You don't have permission for this action",
                undefined
              );
            }
            break;

          case 404:
            // Only toast for write operations; GETs let components handle empty states.
            if (isWrite) {
              this.toast.warning(serverMsg || 'The requested item was not found');
            }
            break;

          case 408:
          case 504:
            this.toast.error('Request timed out — server is slow. Please try again.');
            break;

          case 409:
            this.toast.warning(
              serverMsg || 'Conflict — this action conflicts with existing data'
            );
            break;

          case 400:
          case 422:
            this.toast.warning(serverMsg || 'Please check your input and try again');
            break;

          case 429: {
            // R1 (TC-0009): the server's sentence; without one, the Retry-After wait when it was sent.
            if (SELF_HANDLED_429_URL.test(req.url)) break;
            const wait = retryAfterSeconds(err);
            this.toast.warning(serverMsg || (wait
              ? `Too many requests — please wait ${waitText(wait)} and try again.`
              : 'Too many requests — please slow down'));
            break;
          }

          case 500:
          case 502:
          case 503:
            this.toast.error(
              'Server error — our team has been notified. Please try again shortly.'
            );
            break;

          default:
            this.toast.error(serverMsg || `Request failed (${err.status})`);
        }

        return throwError(() => err);
      })
    );
  }

  private extractServerMessage(err: HttpErrorResponse): string {
    const body = err.error;
    if (!body) return '';
    if (typeof body === 'string') return body;
    return body.message || body.title || body.error || body.detail || '';
  }
}
